import React from 'react';
import getCurrency from '../../utils/getCurrency';

const InvoiceTemplate = ({ 
  invoiceNumber = "ZET1001",
  amountDue = "$2,700",
  designAmount = "$270.000",
  shippingAmount = "$40.500",
  salesTax = "",
  salesTaxAmount = "",
  totalWithTax = "",
  className = "",
  propertyData = null,
  buyerTotal = 0,
  designCredit = 0
}) => {
  // Format current date for invoice date
  const invoiceDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Calculate payment due date as 3 days after the invoice date
  const paymentDueDateObj = new Date();
  paymentDueDateObj.setDate(paymentDueDateObj.getDate() + 3);
  const paymentDueDate = paymentDueDateObj.toLocaleDateString('en-US', { 
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div
      className={`invoice-template bg-white p-8 max-w-[8.5in] mx-auto ${className}`}
      style={{ fontFamily: "Arial, sans-serif" }}
    >
      {/* Header Section - Keep together */}
      <div className="block items-center gap-4">
        {/* <div className="text-3xl font-bold tracking-wide">REVERIE3D</div> */}
        <img src={"/logo.svg"} alt="Reverie3D" className="w-48" />
      </div>
      <div className="invoice-section keep-together flex justify-end items-start mb-8">
        <div className="text-right">
          <h1 className="section-header text-4xl font-bold text-gray-800 mb-2">
            INVOICE
          </h1>
          <div className="text-sm text-gray-600">
            <div className="mb-1">Reverie3D LLC</div>
            <div className="mb-1">1 Sansome Street - Suite 1400</div>
            <div className="mb-1">San Francisco, California 94104</div>
            <div className="mb-1">United States</div>
            <div className="mb-1">(415) 742-2409</div>
            <div>www.reverie3d.com</div>
          </div>
        </div>
      </div>

      {/* Property Information - Keep together */}
      {propertyData && (
        <div className="invoice-section keep-together mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="section-header text-sm font-semibold text-gray-700 mb-2">
            Property Information
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">Property Name:</span>{" "}
              {propertyData.property_name}
            </div>
            <div>
              <span className="font-medium">Property Type:</span>{" "}
              {propertyData.property_type}
            </div>
            <div>
              <span className="font-medium">Location:</span> {propertyData.city}
              , {propertyData.state}
            </div>
            <div>
              <span className="font-medium">Property ID:</span>{" "}
              {propertyData.id}
            </div>
          </div>
        </div>
      )}

      {/* Invoice Details Section - Keep together */}
      <div className="invoice-section keep-together grid grid-cols-2 gap-8 mb-8">
        <div>
          <div className="mb-4">
            <div className="text-sm font-semibold text-gray-700 mb-1">
              Invoice Number:
            </div>
            <div className="text-lg font-bold">{invoiceNumber}</div>
          </div>
          <div className="mb-4">
            <div className="text-sm font-semibold text-gray-700 mb-1">
              Invoice Date:
            </div>
            <div className="text-lg">{invoiceDate}</div>
          </div>
        </div>
        <div>
          <div className="mb-4">
            <div className="text-sm font-semibold text-gray-700 mb-1">
              Payment Due:
            </div>
            <div className="text-lg">{paymentDueDate}</div>
          </div>
          <div className="mb-4">
            <div className="text-sm font-semibold text-gray-700 mb-1">
              Amount Due (USD):
            </div>
            <div className="text-2xl font-bold text-gray-800">
            {totalWithTax || (buyerTotal > 0 ? getCurrency(buyerTotal) : amountDue)}
            </div>
          </div>
        </div>
      </div>

      {/* Notice - Keep together */}
      <div className="invoice-section keep-together text-sm text-gray-600 italic mb-8">
        Final amount due to be finalized at project completion. Payment is
        required to secure final product selection.
      </div>

      {/* Items Table - Smart page breaking */}
      <div className="invoice-section mb-8">
        <table className="invoice-table w-full">
          <thead className="keep-together">
            <tr className="bg-gray-800 text-white">
              <th className="text-left py-3 px-4 font-semibold">Item</th>
              <th className="text-end py-3 px-4 font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody>
            <>
              <tr className="item-row border-b border-gray-200">
                <td className="py-3 px-4">
                  Design, 3D Rendering Services & Goods
                </td>
                <td className="py-3 px-4 text-right">{designAmount}</td>
              </tr>
              <tr className="item-row border-b border-gray-200">
                <td className="py-3 px-4">
                  Shipping & Installation
                </td>
                <td className="py-3 px-4 text-right">{shippingAmount}</td>
              </tr>
              {designCredit > 0 && (
                <tr className="item-row border-b border-gray-200">
                  <td className="py-3 px-4">
                    Design Credit
                  </td>
                  <td className="py-3 px-4 text-right">-{getCurrency(designCredit)}</td>
                </tr>
              )}
              {salesTaxAmount && salesTax && (
                <tr className="item-row border-b border-gray-200">
                  <td className="py-3 px-4">
                    Sales Tax ({salesTax})
                  </td>
                  <td className="py-3 px-4 text-right">{salesTaxAmount}</td>
                </tr>
              )}
            </>

            {/* Total row - keep with at least one item above */}
            <tr className="total-row keep-together bg-gray-50 font-bold">
              <td className="py-3 px-4 text-right">
                Amount Due
              </td>
              <td className="py-3 px-4 text-right">
                {totalWithTax || (buyerTotal > 0 ? getCurrency(buyerTotal) : amountDue)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Notes/Terms Section - Keep together or allow break if too long */}
      <div className="invoice-section mt-8 page-break-before-if-needed">
        <div className="section-header text-sm font-semibold text-gray-700 mb-2">
          Notes / Terms
        </div>
        <div className="notes-content text-sm text-gray-600 space-y-1">
          <div>
            <strong>Domestic bank & wire transfer details:</strong>
          </div>
          <div>Bank: Chase Bank</div>
          <div>Routing #: 322 271 627</div>
          <div>Account #: 960 359 027</div>
          <div>ABA# or ACH#: 021 400 321</div>
          <div>Beneficiary: Reverie3D LLC</div>
        </div>
      </div>

      {/* Page break control styles */}
      <style jsx>{`
        /* Page break control classes */
        .invoice-section {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          margin-bottom: 1rem;
        }

        .keep-together {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }

        .section-header {
          page-break-after: avoid !important;
          break-after: avoid !important;
        }

        .invoice-table {
          page-break-inside: auto !important;
          break-inside: auto !important;
          border-collapse: collapse;
          table-layout: fixed;
        }

        .invoice-table th,
        .invoice-table td {
          vertical-align: top;
        }

        .invoice-table th:nth-child(1),
        .invoice-table td:nth-child(1) {
          width: 70%;
        }

        .invoice-table th:nth-child(2),
        .invoice-table td:nth-child(2) {
          width: 30%;
        }

        .invoice-table thead {
          page-break-after: avoid !important;
          break-after: avoid !important;
          display: table-header-group !important;
        }

        .item-row {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }

        .total-row {
          page-break-before: avoid !important;
          break-before: avoid !important;
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }

        .notes-content {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }

        .page-break-before-if-needed {
          page-break-before: auto !important;
          break-before: auto !important;
        }

        /* Ensure proper spacing and orphan/widow control */
        @media print {
          .invoice-template {
            font-size: 12pt !important;
            line-height: 1.4 !important;
          }

          .invoice-section {
            orphans: 2;
            widows: 2;
          }

          /* Keep table headers with content */
          .invoice-table thead tr {
            page-break-after: avoid !important;
            break-after: avoid !important;
          }

          /* Avoid breaking immediately after headers */
          .section-header + * {
            page-break-before: avoid !important;
            break-before: avoid !important;
          }

          /* Keep total with at least one item */
          .total-row {
            orphans: 3;
          }

          /* Prevent single line at top/bottom of page */
          p,
          div {
            orphans: 2;
            widows: 2;
          }
        }

        /* Additional responsive adjustments */
        @media screen and (max-width: 8.5in) {
          .invoice-template {
            max-width: 100% !important;
            padding: 1rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default InvoiceTemplate;